import React, { useState, useEffect, FC, useCallback } from "react";
import {
  DataGrid,
  GridColDef,
  GridCsvExportOptions,
  GridToolbarContainer,
  useGridApiContext,
} from "@mui/x-data-grid";
import "./ExcelDataGrid.css";
import ErrorList from "../ErrorList";
import { PaginationDatagrid } from "../PaginationDatagrid";
import { Box, Typography } from "@mui/material";

interface Props {
  data: { [key: string]: any }[];
  onRowUpdate: (updatedRow: any) => void;
  onInvalidCellsChange: (invalidCells: { [key: string]: string }) => void;
  selectedRow: number | null;
  selectedColumn: string | null;
  apiRef: any;
  index1: any;
  index2: any;
  title: string; // Added title prop
}

//// Purpose of the component Excel Data Grid : Display the data grid and verifiy the validity in the the front first

const ExcelDataGrid: React.FC<Props> = ({
  data,
  onRowUpdate,
  onInvalidCellsChange,
  apiRef,
  index1,
  index2,
  title, // Destructure the title prop
}) => {
  const [invalidCells, setInvalidCells] = useState<{ [key: string]: string }>(
    {}
  );

  useEffect(() => {
    const updatedInvalidCells: any = {};

    data.forEach((row) => {
      validateCell(row, updatedInvalidCells);
    });

    const invalidCellsChanged =
      JSON.stringify(updatedInvalidCells) !== JSON.stringify(invalidCells);

    if (invalidCellsChanged) {
      setInvalidCells(updatedInvalidCells);
      onInvalidCellsChange(updatedInvalidCells);
    }
  }, [data, onInvalidCellsChange]);

  if (!data || data.length === 0) {
    return <div></div>;
  }

  const handleCellEditStart = (params: any) => {
    console.log("event triggered", index1, index2);

    apiRef.current.scrollToIndexes({
      rowIndex: index1,
      colIndex: index2,
    });
  };

  const columns: GridColDef[] = Object.keys(data[0])
    .filter((key) => key !== "id")
    .map((key) => {
      const minWidth = Math.max(key.length * 10, 100); // Adjust multiplier and minimum width as needed
      return {
        field: key,
        headerName: key,
        minWidth: minWidth,
        flex: 0.5,
        editable: true,
      };
    });

  const rowsWithIds = data.map((row, index) => ({
    id: index + 1,
    ...row,
  }));

  const processRowUpdate = (newRow: any) => {
    const updatedRow = { ...newRow };
    const updatedInvalidCells = { ...invalidCells };
    for (const key in updatedRow) {
      if (Object.prototype.hasOwnProperty.call(updatedRow, key)) {
        const value = updatedRow[key];
        if (typeof value === "string" && /^\d+(\.\d+)?$/.test(value)) {
          updatedRow[key] = parseFloat(value);
        }
      }
    }

    if (
      updatedRow.REMQTE !== undefined ||
      updatedRow.Reduction1 !== undefined ||
      updatedRow.Reduction2 !== undefined ||
      updatedRow.Reduction3 !== undefined
    ) {
      validateCell(updatedRow, updatedInvalidCells);
    }

    onRowUpdate(updatedRow);

    return updatedRow;
  };

  const getRowClassName = (params: any) => {
    const rowId = params.id;
    const hasInvalidCells = Object.keys(invalidCells).some((key) =>
      key.startsWith(`${rowId}-`)
    );
    return hasInvalidCells ? "invalid-row" : "";
  };
  const getCellClassName = (params: any) => {
    const cellId = `${params.row.id}-${params.field}`;
    const isError = invalidCells[cellId];
    return isError ? "invalid-cell" : "";
  };

  const getCellTooltip = (params: any) => {
    const cellId = `${params.row.id}-${params.field}`;
    const error = invalidCells[cellId];

    return error ? error : "";
  };
  interface ValidationRule {
    errorMessage: string;
    validate: (value: any, row?: any) => boolean;
    allowEmpty: boolean;
  }
  const validateCell = (
    row: {
      [key: string]: any;
    },
    updatedInvalidCells: {
      [x: string]: string;
    }
  ) => {
    const validateXChar = (
      numberofChar: number,
      allowEmpty: boolean,
      defaultValue: any = null // default value
    ): ValidationRule => ({
      errorMessage: ErrorList.errorLength + " (" + numberofChar + " max)",
      validate: (value: any) => {
        if (
          allowEmpty &&
          (value === null || value === undefined || value === "")
        ) {
          return true; // Allow empty values
        }
        value =
          value === null || value === undefined || value === ""
            ? defaultValue
            : value;
        return (
          (typeof value === "string" && value.length <= numberofChar) ||
          (typeof value === "number" &&
            Number.isInteger(value) &&
            value.toString().length <= numberofChar)
        );
      },
      allowEmpty: allowEmpty,
    });

    const validateExactChar = (
      numberofChar: number,
      allowEmpty: boolean,
      defaultValue: any = null // default value
    ): ValidationRule => ({
      errorMessage:
        ErrorList.errorLength + " (exactement " + numberofChar + ")",
      validate: (value: any) => {
        if (
          allowEmpty &&
          (value === null || value === undefined || value === "")
        ) {
          return true; // Allow empty values
        }
        value =
          value === null || value === undefined || value === ""
            ? defaultValue
            : value;
        return (
          (typeof value === "string" && value.length === numberofChar) ||
          (typeof value === "number" &&
            Number.isInteger(value) &&
            value.toString().length === numberofChar)
        );
      },
      allowEmpty: allowEmpty,
    });

    const validateNumber = (
      allowEmpty: boolean,
      defaultValue: any = null // default value
    ): ValidationRule => ({
      errorMessage: ErrorList.errorDigit,
      validate: (value: any) => {
        if (
          allowEmpty &&
          (value === null || value === undefined || value === "")
        ) {
          return true; // Allow empty values
        }
        value =
          value === null || value === undefined || value === ""
            ? defaultValue
            : value;
        return typeof value === "number" && !isNaN(value);
      },
      allowEmpty: allowEmpty,
    });

    const validateBool = (
      allowEmpty: boolean,
      defaultValue: any = null // default value
    ): ValidationRule => ({
      errorMessage: ErrorList.errorBoolean,
      validate: (value: any) => {
        if (
          allowEmpty &&
          (value === null || value === undefined || value === "")
        ) {
          return true; // Allow empty values
        }
        value =
          value === null || value === undefined || value === ""
            ? defaultValue
            : value;
        return (
          (typeof value === "string" && value === "O") ||
          (typeof value === "string" && value === "N")
        );
      },
      allowEmpty: allowEmpty,
    });
    const validatePercent = (
      allowEmpty: boolean,
      defaultValue: any = null // default value
    ): ValidationRule => ({
      errorMessage: " Valeur possible :V ou P",
      validate: (value: any) => {
        if (
          allowEmpty &&
          (value === null || value === undefined || value === "")
        ) {
          return true; // Allow empty values
        }
        value =
          value === null || value === undefined || value === ""
            ? defaultValue
            : value;
        return (
          (typeof value === "string" && value === "P") ||
          (typeof value === "string" && value === "V")
        );
      },
      allowEmpty: allowEmpty,
    });
    const validationRules: { [key: string]: ValidationRule } = {
      "Code société": validateExactChar(2, false),
      ArticleSoc: validateXChar(2, false), // Using the same validation function for ArticleSoc
      //
      "Code Article": validateXChar(8, false),
      ArticleCode: validateXChar(8, false),
      //
      "Ancien Code": validateXChar(8, true),
      ArticleOld: validateXChar(8, true),
      //
      "Libellé 1": validateXChar(40, false),
      ArticleName: validateXChar(40, false),
      //
      "Libellé 2": validateXChar(40, true),
      ArticleName2: validateXChar(40, true),
      //
      "Grp de famille": validateXChar(2, false),
      Group: validateXChar(2, false),
      //
      Famille: validateXChar(3, false),
      Family: validateXChar(3, false),
      //
      BuyingUnity: validateXChar(2, false),
      UA: validateXChar(2, false),
      //
      StockUnity: validateXChar(2, false),
      US: validateXChar(2, false),
      //
      SalesUnity: validateXChar(2, false),
      UV: validateXChar(2, false),
      //
      "US/UA": validateNumber(false),
      UnityBuyingOnSales: validateNumber(false),
      //
      UnityStockOnSales: validateNumber(false),
      "US/UV": validateNumber(false),
      //
      Provider: validateNumber(false),
      "Code FRN": validateNumber(false),
      //
      "Ref FRN": validateXChar(20, false),
      ReferenceProvider: validateXChar(20, false),
      //
      Gencod: validateXChar(13, false),
      //
      "Gencod 2": validateXChar(13, true),
      Gencod2: validateXChar(13, true),
      //
      "Gencod 3": validateXChar(13, true),
      Gencod3: validateXChar(13, true),
      //

      "prix achat brut": validateNumber(false),
      GrossBuyingPrice: validateNumber(false),
      //

      "Remise 1": validateNumber(true),
      Reduction1: validateNumber(true),
      //
      "Valeur pourcent (V/P)": validatePercent(true),
      PercentOnValueReduction1: validatePercent(true),
      //
      "Remise 2": validateNumber(true),
      Reduction2: validateNumber(true),
      //
      "Valeur pourcent": validatePercent(true),
      PercentOnValueReduction2: validatePercent(true),
      //
      "Remise 3": validateNumber(true),
      Reduction3: validateNumber(true),
      //
      "Valeur pourcent _v": validatePercent(true),
      PercentOnValueReduction3: validatePercent(true),
      //
      "Prix d'achat net": validateNumber(false),
      NetBuyingPrice: validateNumber(false),
      //
      "Prix de base": validateNumber(true),
      BasePrice: validateNumber(true),
      //
      "Prix vente barème A": validateNumber(false),
      SalesPriceWithoutTaxesA: validateNumber(false),
      //
      B: validateNumber(false),
      SalesPriceWithoutTaxesB: validateNumber(false),
      //
      C: validateNumber(false),
      SalesPriceWithoutTaxesC: validateNumber(false),
      //
      D: validateNumber(false),
      SalesPriceWithoutTaxesD: validateNumber(false),
      //
      "Coef A": validateNumber(false),
      CoefficientA: validateNumber(false),
      //
      "Coef B": validateNumber(false),
      CoefficientB: validateNumber(false),
      //
      "Coef C": validateNumber(false),
      CoefficientC: validateNumber(false),
      //
      "Coef D": validateNumber(false),
      CoefficientD: validateNumber(false),
      //
      //
      Qui: validateXChar(10, false),
      ModifiedBy: validateXChar(10, false),
      //

      TAXTYP: {
        errorMessage: "TAXTYP doit être une chaîne de caractères valide",
        validate: (value: any) =>
          typeof value === "string" && value.trim() !== "",
        allowEmpty: true,
      },
      TAXMUA: {
        errorMessage: "TAXMUA doit être un nombre positif",
        validate: (value: any) => typeof value === "number" && value >= 0,
        allowEmpty: true,
      },
      TAXCDO: {
        errorMessage: "TAXCDO doit être une chaîne de caractères valide",
        validate: (value: any) =>
          typeof value === "string" && value.trim() !== "",
        allowEmpty: true,
      },

      Quand: {
        errorMessage: ErrorList.ModifiedOn,

        validate: (value: any) => {
          if (typeof value === "string") {
            // Regular expression for DD/MM/YYYY format
            const ddMmYyyyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|[2-9][0-9])\d\d$/;

            // Regular expression for YYYY-MM-DD format
            const yyyyMmDdRegex =
              /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
            // Regular expression for DD/MM/YY format
            const ddMmYyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{2})$/;

            // Validate if the date matches either format
            return (
              ddMmYyyyRegex.test(value) ||
              yyyyMmDdRegex.test(value) ||
              ddMmYyRegex.test(value)
            );
          } else {
            return false;
          }
        },
        allowEmpty: false,
      },

      ModifiedOn: {
        errorMessage: ErrorList.ModifiedOn,
        validate: (value: any) => {
          if (typeof value === "string") {
            // Regular expression for DD/MM/YYYY format
            const ddMmYyyyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|[2-9][0-9])\d\d$/;

            // Regular expression for YYYY-MM-DD format
            const yyyyMmDdRegex =
              /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
            // Regular expression for DD/MM/YY format
            const ddMmYyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{2})$/;

            // Validate if the date matches either format
            return (
              ddMmYyyyRegex.test(value) ||
              yyyyMmDdRegex.test(value) ||
              ddMmYyRegex.test(value)
            );
          } else {
            return false;
          }
        },
        allowEmpty: false,
      },
      //

      "Arrondi PA net": validateXChar(1, false),
      RoundedBuyingPrice: validateXChar(1, false),
      //
      "Arrondie PXB": validateXChar(1, true),
      RoundedGrossPrice: validateXChar(1, true),
      //
      "Prix de revient": validateNumber(true),
      CostPrice: validateNumber(true),
      //
      RoundedCostPrice: validateXChar(1, false),
      "Arrondi PR": validateXChar(1, false),
      //

      "Cout TP": validateNumber(true),
      TRSOnUnityAmmount: validateNumber(false),
      //
      Lot: validateBool(false),
      "LS CMEM": {
        errorMessage: "La valeur doit être 'O' ou 'N'",
        validate: (value: any) => value === "O" || value === "N",
        allowEmpty: false,
      },
      //
      "Code TVA": validateNumber(false),
      VATCode: validateNumber(false),
      //
      "E-Commerce": {
        errorMessage: "La valeur doit être 'O', 'N' ou 'I'",
        validate: (value: any) =>
          value === "O" || value === "N" || value === "I",
        allowEmpty: false,
      },
      //
      "Paquets a la vente": validateNumber(false),
      PacketForSale: validateNumber(false),
      //
      "Code Centrale achat": validateXChar(20, false),
      BuyingCentralCode: validateXChar(20, false),
      //

      "Article LS": validateBool(false),
      LSArticle: validateBool(false),

      COLISEE: {
        errorMessage:
          "La valeur doit être 'O', 'C', 'I', 'E', 'G', 'F', 'H', 'K', 'J', 'X' ou vide",
        validate: (value: any) =>
          ["O", "C", "I", "E", "G", "F", "H", "K", "J", "X", ""].includes(
            value
          ),
        allowEmpty: true,
      },
      //
      DLUO: validateBool(true),
      ExpiryPeriod: validateBool(true),
      //
      Dangereux: validateBool(false),
      HazardousMaterial: validateBool(false),
      //
      "Craint le gele": validateBool(false),
      FreezingProduct: validateBool(false),
      //
      "Longueur ": validateNumber(true),
      PackageLength: validateNumber(true),
      //
      Largeur: validateNumber(true),
      PackageWidth: validateNumber(true),
      //
      Hauteur: validateNumber(true),
      PackageHeight: validateNumber(true),
      //
      "NB COLIS PAR COUCHE": validateNumber(true),
      NbPackagePerLayer: validateNumber(true),
      //
      "COUCHE PAR PAL": validateNumber(true),
      NbLayerPerPalette: validateNumber(true),
      //
      "Hauteur Couche": validateNumber(true),
      LayerHeight: validateNumber(true),
      //
      Bain: validateBool(false),
      Bath: validateBool(false),

      Calibre: validateBool(false),
      Caliber: validateBool(false),
      //
      Approvisioinneur: validateXChar(10, false),
      Supplier: validateXChar(10, false),
      //
      "Vente annuelle": validateNumber(false),
      AnnualRequestFixed: validateNumber(false),
      //
      "Delai FRN": validateNumber(false),
      SupplierDelay: validateNumber(false),
      "méthode delai": {
        errorMessage: "La valeur doit être égale à '0' ou '1 '",
        validate: (value: any) =>
          value === "0" || value === 0 || value === "1" || value === 1,
        allowEmpty: false,
      },
      PrevisionMethod: {
        errorMessage: "La valeur doit être égale à '0' ou '1'",
        validate: (value: any) =>
          value === "0" || value === 0 || value === "1" || value === 1,
        allowEmpty: false,
      },
      //
      ARTDAF: validateNumber(true),
      RFACoeff: validateNumber(true),
      "Coeff. Revente (RFA)": validateNumber(true),
      //
      "Prix plancher": validateNumber(true),
      MinimalPrice: validateNumber(true),
      // "delai avant benne DLUO": {
      //   errorMessage: ErrorList.errorLength + "(10 max) ",
      //   validate: (value: any) =>
      //     typeof value === "string" && value.length <= 10,
      //   allowEmpty: false,
      // },
      "Longeur palette": validateNumber(true),
      PaletteLength: validateNumber(true),
      //
      "largeur palette": validateNumber(true),
      PaletteWidth: validateNumber(true),
      //
      "Hauteur palette": validateNumber(true),
      PaletteHeight: validateNumber(true),
      //
      "Poids palette": validateNumber(true),
      PaletteWeight: validateNumber(true),
      //
      "Unit US/UA": {
        errorMessage: "CVSRFUA doit être égale à UA ou à US",
        validate: (value: any, row: any) => {
          return value === row.UA || value === row.US;
        },
        allowEmpty: false,
      },

      "Unit US/UV": {
        errorMessage: "CVSRFSV doit être égale à UV ou à US",
        validate: (value: any, row: any) => {
          return value === row.UV || value === row.US;
        },
        allowEmpty: false,
      },
      // Un.RempD1, Un.RempG1, Valeur Un.Remp1: soit toutes vides, soit toutes renseignées, et RempD1 ou RempG1 doit être UA, US ou UV
      "Un.RempD1": {
        errorMessage: "Un.RempD1 doit être UA, US ou UV",
        validate: (value: any, row: any) => {
          const d1 = row["Un.RempD1"];
          const g1 = row["Un.RempG1"];
          const v1 = row["Valeur Un.Remp1"];
          const allEmpty = [d1, g1, v1].every((v) => !v && v !== 0);
          const allFilled = [d1, g1, v1].every((v) => v || v === 0);
          if (allEmpty) return true;
          if (!allFilled) return false;
          // At least one of d1 or g1 must be UA, US, or UV
          return (
            d1 === row.UA ||
            d1 === row.US ||
            d1 === row.UV ||
            g1 === row.UA ||
            g1 === row.US ||
            g1 === row.UV
          );
        },
        allowEmpty: true,
      },
      "Un.RempG1": {
        errorMessage: "Un.RempG1 doit être UA, US ou UV",
        validate: (value: any, row: any) => {
          const d1 = row["Un.RempD1"];
          const g1 = row["Un.RempG1"];
          const v1 = row["Valeur Un.Remp1"];
          const allEmpty = [d1, g1, v1].every((v) => !v && v !== 0);
          const allFilled = [d1, g1, v1].every((v) => v || v === 0);
          if (allEmpty) return true;
          if (!allFilled) return false;
          // At least one of d1 or g1 must be UA, US, or UV
          return (
            d1 === row.UA ||
            d1 === row.US ||
            d1 === row.UV ||
            g1 === row.UA ||
            g1 === row.US ||
            g1 === row.UV
          );
        },
        allowEmpty: true,
      },
      "Valeur Un.Remp1": {
        errorMessage:
          "Si renseigné, Un.RempD1 ou Un.RempG1 doit être UA, US ou UV et les 3 champs doivent être tous renseignés",
        validate: (value: any, row: any) => {
          const d1 = row["Un.RempD1"];
          const g1 = row["Un.RempG1"];
          const v1 = row["Valeur Un.Remp1"];
          const allEmpty = [d1, g1, v1].every((v) => !v && v !== 0);
          const allFilled = [d1, g1, v1].every((v) => v || v === 0);
          if (allEmpty) return true;
          if (!allFilled) return false;
          // At least one of d1 or g1 must be UA, US, or UV
          return (
            d1 === row.UA ||
            d1 === row.US ||
            d1 === row.UV ||
            g1 === row.UA ||
            g1 === row.US ||
            g1 === row.UV
          );
        },
        allowEmpty: true,
      },
      "Un.RempD2": {
        errorMessage:
          "Si renseigné, Un.RempD2 ou Un.RempG2 doit être UA, US ou UV et les 3 champs doivent être tous renseignés",
        validate: (value: any, row: any) => {
          const d2 = row["Un.RempD2"];
          const g2 = row["Un.RempG2"];
          const v2 = row["Valeur Un.Remp2"];
          const allEmpty = [d2, g2, v2].every((v) => !v && v !== 0);
          const allFilled = [d2, g2, v2].every((v) => v || v === 0);
          if (allEmpty) return true;
          if (!allFilled) return false;
          // At least one of d2 or g2 must be UA, US, or UV
          return (
            d2 === row.UA ||
            d2 === row.US ||
            d2 === row.UV ||
            g2 === row.UA ||
            g2 === row.US ||
            g2 === row.UV
          );
        },
        allowEmpty: true,
      },
      "Un.RempG2": {
        errorMessage:
          "Si renseigné, Un.RempD2 ou Un.RempG2 doit être UA, US ou UV et les 3 champs doivent être tous renseignés",
        validate: (value: any, row: any) => {
          const d2 = row["Un.RempD2"];
          const g2 = row["Un.RempG2"];
          const v2 = row["Valeur Un.Remp2"];
          const allEmpty = [d2, g2, v2].every((v) => !v && v !== 0);
          const allFilled = [d2, g2, v2].every((v) => v || v === 0);
          if (allEmpty) return true;
          if (!allFilled) return false;
          // At least one of d2 or g2 must be UA, US, or UV
          return (
            d2 === row.UA ||
            d2 === row.US ||
            d2 === row.UV ||
            g2 === row.UA ||
            g2 === row.US ||
            g2 === row.UV
          );
        },
        allowEmpty: true,
      },
      "Valeur Un.Remp2": {
        errorMessage:
          "Si renseigné, Un.RempD2 ou Un.RempG2 doit être UA, US ou UV et les 3 champs doivent être tous renseignés",
        validate: (value: any, row: any) => {
          const d2 = row["Un.RempD2"];
          const g2 = row["Un.RempG2"];
          const v2 = row["Valeur Un.Remp2"];
          const allEmpty = [d2, g2, v2].every((v) => !v && v !== 0);
          const allFilled = [d2, g2, v2].every((v) => v || v === 0);
          if (allEmpty) return true;
          if (!allFilled) return false;
          // At least one of d2 or g2 must be UA, US, or UV
          return (
            d2 === row.UA ||
            d2 === row.US ||
            d2 === row.UV ||
            g2 === row.UA ||
            g2 === row.US ||
            g2 === row.UV
          );
        },
        allowEmpty: true,
      },
      //
      //
      Conditionnement: validateNumber(false),
      Conditioning: validateNumber(false),
      //
      "Sous-conditionnement": validateNumber(false),
      NumberOfItemsPerPackage: validateNumber(false),
      //
      "Multiple de Cde": validateNumber(false),
      BuyingMultiple: validateNumber(false),
      //
      "Quantité produit/Palette": validateNumber(true),
      ConditioningPalette: validateNumber(true),
      //
      "Nombre de jours de DLV ": validateNumber(true),
      DelayBeforDLUO: validateNumber(true),
      //
      "Durée de vie du produit": validateNumber(true),
      ARTVIE: validateNumber(true),
      //
      "Suivi stock (O/N)": validateBool(false),
      StockCm: validateBool(false),

      REMQTE: validateNumber(true),
      REMTX: validateNumber(true),
      REMDEB: {
        errorMessage: ErrorList.ModifiedOn,
        validate: (value: any, row: any) => {
          if (
            row.REMQTE === null ||
            row.REMQTE === undefined ||
            row.REMQTE === ""
          ) {
            return true; // Allow empty if REMQTE is empty
          }
          if (typeof value === "string") {
            // Regular expression for DD/MM/YYYY format
            const ddMmYyyyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|[2-9][0-9])\d\d$/;

            // Regular expression for YYYY-MM-DD format
            const yyyyMmDdRegex =
              /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
            // Regular expression for DD/MM/YY format
            const ddMmYyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{2})$/;

            // Validate if the date matches either format
            return (
              ddMmYyyyRegex.test(value) ||
              yyyyMmDdRegex.test(value) ||
              ddMmYyRegex.test(value)
            );
          } else {
            return false;
          }
        },
        allowEmpty: true, // Allow empty if REMQTE is empty
      },
      REMFIN: {
        errorMessage: ErrorList.ModifiedOn,
        validate: (value: any, row: any) => {
          if (
            row.REMQTE === null ||
            row.REMQTE === undefined ||
            row.REMQTE === ""
          ) {
            return true; // Allow empty if REMQTE is empty
          }
          if (typeof value === "string") {
            // Regular expression for DD/MM/YYYY format
            const ddMmYyyyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|[2-9][0-9])\d\d$/;

            // Regular expression for YYYY-MM-DD format
            const yyyyMmDdRegex =
              /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9])$/;
            // Regular expression for DD/MM/YY format
            const ddMmYyRegex =
              /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{2})$/;

            // Validate if the date matches either format
            return (
              ddMmYyyyRegex.test(value) ||
              yyyyMmDdRegex.test(value) ||
              ddMmYyRegex.test(value)
            );
          } else {
            return false;
          }
        },
        allowEmpty: true, // Allow empty if REMQTE is empty
      },
      //
      "Info tarif": {
        errorMessage: "Valeurs possibles : C, D ou vide",
        validate: (value: any) => {
          return value === "C" || value === "D" || value === "";
        },
        allowEmpty: true,
      },
      HFLGTT: {
        errorMessage: "Valeurs possibles : C, D ou vide",
        validate: (value: any) => {
          return value === "C" || value === "D" || value === "";
        },
        allowEmpty: true,
      },
      "Affichage pp Hub": {
        errorMessage: "Valeur possible : O ou vide",
        validate: (value: any) => {
          return value === "O" || value === "";
        },
        allowEmpty: true,
      },
      HFLGPP: {
        errorMessage: "Valeur possible : O ou vide",
        validate: (value: any) => {
          return value === "O" || value === "";
        },
        allowEmpty: true,
      },
    };

    for (const key in row) {
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        const rule = validationRules[key];
        if (rule) {
          const value = row[key];

          if (!value && rule.allowEmpty) {
            // Skip validation if value is empty and allowEmpty is true
            delete updatedInvalidCells[`${row.id}-${key}`];
          } else if (
            (value === null || value === undefined || value === "") &&
            !rule.allowEmpty
          ) {
            updatedInvalidCells[
              `${row.id}-${key}`
            ] = ` Ce champ ne doit pas etre vide `;
          } else if (!rule.validate(value, row)) {
            updatedInvalidCells[`${row.id}-${key}`] = ` ${rule.errorMessage} `;
          } else {
            delete updatedInvalidCells[`${row.id}-${key}`];
          }
        }
      }
    }

    // Additional validation to check if REMDEB is less than REMFIN
    if (
      row.REMQTE &&
      (row.REMDEB === null || row.REMDEB === undefined || row.REMDEB === "")
    ) {
      updatedInvalidCells[`${row.id}-REMDEB`] =
        "REMDEB doit être renseigné si REMQTE est renseigné";
    }

    // Validation for TAXTYP
    if (row.TAXTYP && typeof row.TAXTYP !== "string") {
      updatedInvalidCells[`${row.id}-TAXTYP`] =
        "TAXTYP doit être une chaîne de caractères valide";
    }
    // Validation for TAXCDO
    if (
      row.TAXCDO &&
      (typeof row.TAXCDO !== "string" || row.TAXCDO.length !== 2)
    ) {
      updatedInvalidCells[`${row.id}-TAXCDO`] =
        "TAXCDO doit être une chaîne de 2 caractères";
    }

    // Validation for TAXORG
    if (row.TAXORG && typeof row.TAXORG !== "number") {
      updatedInvalidCells[`${row.id}-TAXORG`] = "TAXORG doit être un nombre";
    }
    // Validation for TAXMUA
    if (row.TAXMUA && (typeof row.TAXMUA !== "number" || row.TAXMUA < 0)) {
      updatedInvalidCells[`${row.id}-TAXMUA`] =
        "TAXMUA doit être un nombre positif";
    }

    if (
      row.REMQTE &&
      (row.REMFIN === null || row.REMFIN === undefined || row.REMFIN === "")
    ) {
      updatedInvalidCells[`${row.id}-REMFIN`] =
        "REMFIN doit être renseigné si REMQTE est renseigné";
    }
    if ((row.REMDEB && !row.REMQTE) || (row.REMFIN && !row.REMQTE)) {
      updatedInvalidCells[`${row.id}-REMDEB`] =
        "REMQTE doit être renseigné si REMDEB ou REMFIN est renseigné";
      updatedInvalidCells[`${row.id}-REMFIN`] =
        "REMQTE doit être renseigné si REMDEB ou REMFIN est renseigné";
    }
    if (row.REMDEB && row.REMFIN) {
      const parseDate = (dateStr: any) => {
        const parts = dateStr.split("/");
        if (parts.length !== 3) return null; // Validate format

        let day = parseInt(parts[0], 10);
        let month = parseInt(parts[1], 10) - 1; // Months are 0-based in JS
        let year = parseInt(parts[2], 10);

        // Handle two-digit years (assume 2000+ for years < 50)
        year += year < 50 ? 2000 : 1900;

        return new Date(year, month, day);
      };

      const remdebDate = parseDate(row.REMDEB);
      const remfinDate = parseDate(row.REMFIN);

      if (remdebDate && remfinDate && remdebDate >= remfinDate) {
        updatedInvalidCells[`${row.id}-REMDEB`] =
          "REMDEB doit être inférieur à REMFIN";
        updatedInvalidCells[`${row.id}-REMFIN`] =
          "REMFIN doit être supérieur à REMDEB";
      }
    }

    // Calculate net buying price
    const grossBuyingPrice = parseFloat(row["prix achat brut"]) || 0;
    const reduction1 = parseFloat(row["Remise 1"]) || 0;
    const reduction2 = parseFloat(row["Remise 2"]) || 0;
    const reduction3 = parseFloat(row["Remise 3"]) || 0;

    let calculatedNetBuyingPrice = grossBuyingPrice;
    calculatedNetBuyingPrice -= calculatedNetBuyingPrice * (reduction1 / 100);
    calculatedNetBuyingPrice -= calculatedNetBuyingPrice * (reduction2 / 100);
    calculatedNetBuyingPrice -= calculatedNetBuyingPrice * (reduction3 / 100);

    const netBuyingPriceValue = parseFloat(row["Prix d'achat net"]);
    if (
      isNaN(netBuyingPriceValue) ||
      Math.abs(netBuyingPriceValue - calculatedNetBuyingPrice) > 0.009
    ) {
      updatedInvalidCells[
        `${row.id}-Prix d'achat net`
      ] = `Le prix d'achat net doit être ${calculatedNetBuyingPrice.toFixed(
        2
      )}€`;
    } else {
      delete updatedInvalidCells[`${row.id}-Prix d'achat net`];
    }

    // Ensure prices are decreasing (A >= B >= C >= D) and correspond with coefficients
    const salesPriceA = parseFloat(row["Prix vente barème A"]) || 0;
    const salesPriceB = parseFloat(row["B"]) || 0;
    const salesPriceC = parseFloat(row["C"]) || 0;
    const salesPriceD = parseFloat(row["D"]) || 0;

    const coefA = parseFloat(row["Coef A"]) || 1;
    const coefB = parseFloat(row["Coef B"]) || 1;
    const coefC = parseFloat(row["Coef C"]) || 1;
    const coefD = parseFloat(row["Coef D"]) || 1;

    const expectedPriceB = (salesPriceA * coefB) / coefA;
    const expectedPriceC = (salesPriceA * coefC) / coefA;
    const expectedPriceD = (salesPriceA * coefD) / coefA;

    if (
      !(
        salesPriceA >= salesPriceB &&
        salesPriceB >= salesPriceC &&
        salesPriceC >= salesPriceD
      )
    ) {
      updatedInvalidCells[`${row.id}-Prix vente barème A`] =
        "Les prix doivent être décroissants (A >= B >= C >= D)";
      updatedInvalidCells[`${row.id}-B`] =
        "Les prix doivent être décroissants (A >= B >= C >= D)";
      updatedInvalidCells[`${row.id}-C`] =
        "Les prix doivent être décroissants (A >= B >= C >= D)";
      updatedInvalidCells[`${row.id}-D`] =
        "Les prix doivent être décroissants (A >= B >= C >= D)";
    } else {
      delete updatedInvalidCells[`${row.id}-Prix vente barème A`];
      delete updatedInvalidCells[`${row.id}-B`];
      delete updatedInvalidCells[`${row.id}-C`];
      delete updatedInvalidCells[`${row.id}-D`];
    }

    if (Math.abs(salesPriceB - expectedPriceB) >= 0.01) {
      updatedInvalidCells[
        `${row.id}-B`
      ] = `Le prix B doit être ${expectedPriceB.toFixed(
        2
      )}€ selon le coefficient`;
    } else {
      delete updatedInvalidCells[`${row.id}-B`];
    }

    if (Math.abs(salesPriceC - expectedPriceC) >= 0.01) {
      updatedInvalidCells[
        `${row.id}-C`
      ] = `Le prix C doit être ${expectedPriceC.toFixed(
        2
      )}€ selon le coefficient`;
    } else {
      delete updatedInvalidCells[`${row.id}-C`];
    }

    if (Math.abs(salesPriceD - expectedPriceD) >= 0.01) {
      updatedInvalidCells[
        `${row.id}-D`
      ] = `Le prix D doit être ${expectedPriceD.toFixed(
        2
      )}€ selon le coefficient`;
    } else {
      delete updatedInvalidCells[`${row.id}-D`];
    }

    // Validation for CVSU1 and CVSRFU1
    // const cvsu1 = row.CVSU1?.toString().trim();
    // const cvsrfu1 = row.CVSRFU1?.toString().trim();
    // const uaUsUvValues = [row.UA, row.US, row.UV];

    // const isCvsu1Valid = uaUsUvValues.includes(cvsu1);
    // const isCvsrfu1Valid = uaUsUvValues.includes(cvsrfu1);
    // Validation for CVSU1, CVSRFU1, and CVSU1UR
    // const cvsu1ur = row.CVSU1UR?.toString().trim();

    // const validateFields1 = (
    //   fields: string[],
    //   conditions: { condition: boolean; errorMessage: string }[]
    // ) => {
    //   fields.forEach((field) => {
    //     const fieldErrors: string[] = [];
    //     conditions.forEach(({ condition, errorMessage }) => {
    //       if (condition) {
    //         fieldErrors.push(errorMessage);
    //       }
    //     });
    //     if (fieldErrors.length > 0) {
    //       updatedInvalidCells[`${row.id}-${field}`] = fieldErrors.join(" | ");
    //     } else {
    //       delete updatedInvalidCells[`${row.id}-${field}`];
    //     }
    //   });
    // };

    // const areFieldsEmptyOrFilled1 = (fields: (string | undefined)[]) =>
    //   fields.every((field) => field === "") || fields.every((field) => field);

    // validateFields1(
    //   ["CVSU1", "CVSRFU1", "CVSU1UR"],
    //   [
    //     {
    //       condition: !areFieldsEmptyOrFilled1([cvsu1, cvsrfu1, cvsu1ur]),
    //       errorMessage:
    //         "Les champs CVSU1, CVSRFU1 et CVSU1UR doivent être soit tous vides, soit tous renseignés.",
    //     },
    //   ]
    // );

    // validateFields1(
    //   ["CVSU1", "CVSRFU1"],
    //   [
    //     {
    //       condition:
    //         (cvsu1 || cvsrfu1) &&
    //         !(isCvsu1Valid && !isCvsrfu1Valid) &&
    //         !(isCvsrfu1Valid && !isCvsu1Valid),
    //       errorMessage:
    //         "L'un des deux champs CVSU1 ou CVSRFU1 doit être égal à UA, US ou UV, et l'autre ne doit pas l'être.",
    //     },
    //   ]
    // );

    // Validation for CVSU2 and CVSRFU2
    // const cvsu2 = row.CVSU2?.toString().trim();
    // const cvsrfu2 = row.CVSRFU2?.toString().trim();

    // const isCvsu2Valid = uaUsUvValues.includes(cvsu2);
    // const isCvsrfu2Valid = uaUsUvValues.includes(cvsrfu2);
    // const cvsu2ur = row.CVSU2UR?.toString().trim();
    // const validateFields = (
    //   fields: string[],
    //   conditions: { condition: boolean; errorMessage: string }[]
    // ) => {
    //   fields.forEach((field) => {
    //     const fieldErrors: string[] = [];
    //     conditions.forEach(({ condition, errorMessage }) => {
    //       if (condition) {
    //         fieldErrors.push(errorMessage);
    //       }
    //     });
    //     if (fieldErrors.length > 0) {
    //       updatedInvalidCells[`${row.id}-${field}`] = fieldErrors.join(" | ");
    //     } else {
    //       delete updatedInvalidCells[`${row.id}-${field}`];
    //     }
    //   });
    // };

    // const areFieldsEmptyOrFilled = (fields: (string | undefined)[]) =>
    //   fields.every((field) => field === "") || fields.every((field) => field);

    // validateFields(
    //   ["CVSU2", "CVSRFU2", "CVSU2UR"],
    //   [
    //     {
    //       condition: !areFieldsEmptyOrFilled([cvsu2, cvsrfu2, cvsu2ur]),
    //       errorMessage:
    //         "Les champs CVSU1, CVSRFU1 et CVSU1UR doivent être soit tous vides, soit tous renseignés.",
    //     },
    //   ]
    // );

    // validateFields(
    //   ["CVSU2", "CVSRFU2"],
    //   [
    //     {
    //       condition:
    //         (cvsu2 || cvsrfu2) &&
    //         !(isCvsu2Valid && !isCvsrfu2Valid) &&
    //         !(isCvsrfu2Valid && !isCvsu2Valid),
    //       errorMessage:
    //         "L'un des deux champs CVSU1 ou CVSRFU1 doit être égal à UA, US ou UV, et l'autre ne doit pas l'être.",
    //     },
    //   ]
    // );

    console.log("invalid", updatedInvalidCells);
    setInvalidCells(updatedInvalidCells);
    onInvalidCellsChange(updatedInvalidCells);
  };

  const CustomToolBar: FC = () => {
    const [loading, setLoading] = useState(false);
    const apiRef = useGridApiContext();

    return (
      <GridToolbarContainer>
        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            {title}
          </Typography>
        </Box>
      </GridToolbarContainer>
    );
  };
  return (
    <div style={{ width: "100%", overflow: "auto" }} className="test">
      <DataGrid
        rows={rowsWithIds}
        columns={columns.map((column) => ({
          ...column,
          renderCell: (params) => (
            <div title={getCellTooltip(params)}>{params.value}</div>
          ),
        }))}
        processRowUpdate={processRowUpdate}
        getRowClassName={getRowClassName}
        getCellClassName={getCellClassName}
        apiRef={apiRef}
        onCellModesModelChange={handleCellEditStart}
        slots={{
          toolbar: () => <CustomToolBar />,
          pagination: PaginationDatagrid,
        }}
        slotProps={{
          toolbar: {},
          pagination: {
            itemsCount: data.length, // Total number of records
            itemsTitle: "éléments",
            itemsperpage: data.length,
            totalPages: 1,
            currentPage: 1,
            setCurrentPage: () => {}, // Ne}w prop to set current page

            // Number of items per page
          },
        }}
      />
    </div>
  );
};

export default ExcelDataGrid;
